import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Separator,
} from "@monbits/ui";
import {
	AlertTriangle,
	Check,
	CheckCircle,
	CircleDashed,
	Clock,
	Copy,
	ExternalLink,
	Radio,
	XCircle,
} from "lucide-react";
import * as React from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { EntityDrawer } from "src/components/entity-drawer";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import type { FundReturn } from "src/providers/fund-return-provider";
import { useBroadcastFundReturn } from "src/queries/fund-return-queries";
import type { FundReturnsOutletContext } from "./fund-returns-list";

const statusConfig: Record<
	FundReturn["status"],
	{
		label: string;
		type: "default" | "info" | "warning" | "success" | "error";
		icon: typeof CheckCircle;
	}
> = {
	pending: { label: "Pending", type: "warning", icon: Clock },
	inProgress: { label: "In Progress", type: "info", icon: CircleDashed },
	completed: { label: "Completed", type: "success", icon: CheckCircle },
	failed: { label: "Failed", type: "error", icon: XCircle },
};

export const FundReturnDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const fundReturnId = id ? Number.parseInt(id, 10) : null;

	const outletContext = useOutletContext<
		FundReturnsOutletContext | undefined
	>();
	const fundReturns = outletContext?.fundReturns ?? [];
	const isLoading = outletContext?.isLoading ?? false;
	const fundReturn = React.useMemo(() => {
		if (fundReturnId === null) {
			return null;
		}
		return fundReturns.find((item) => item.id === fundReturnId) ?? null;
	}, [fundReturns, fundReturnId]);
	const broadcastMutation = useBroadcastFundReturn();

	const [showBroadcastDialog, setShowBroadcastDialog] = React.useState(false);
	const [totpCode, setTotpCode] = React.useState("");
	const [copiedAddress, setCopiedAddress] = React.useState<string | null>(null);

	const handleOpenBroadcastDialog = () => {
		setTotpCode("");
		setShowBroadcastDialog(true);
	};

	const handleCloseBroadcastDialog = () => {
		setShowBroadcastDialog(false);
		setTotpCode("");
	};

	const handleBroadcast = async () => {
		if (!fundReturn) return;

		if (totpCode.trim().length !== 6) {
			toast.error("Invalid 2FA code");
			return;
		}

		try {
			await broadcastMutation.mutateAsync({
				id: fundReturn.id,
				data: { totpCode: totpCode.trim() },
			});
			toast.success("Transaction broadcasted successfully");
			handleCloseBroadcastDialog();
		} catch (error: unknown) {
			console.error("Failed to broadcast fund return:", error);
			const errorMessage =
				(error as { response?: { data?: { message?: string } } })?.response
					?.data?.message || "Failed to broadcast";
			toast.error(errorMessage);
		}
	};

	const handleCopyAddress = (address: string) => {
		navigator.clipboard.writeText(address);
		setCopiedAddress(address);
		toast.success("Address copied");
		setTimeout(() => setCopiedAddress(null), 2000);
	};

	return (
		<>
			<EntityDrawer
				title={`Fund Return #${fundReturnId}`}
				isLoading={isLoading}
				isNotFound={!isLoading && !fundReturn}
				notFoundMessage="Fund return not found"
				parentPath="/fund-returns"
			>
				{fundReturn && (
					<div className="p-6 space-y-6">
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold">Status</h3>
								{(() => {
									const config = statusConfig[fundReturn.status] ?? {
										label: fundReturn.status,
										type: "default" as const,
										icon: Clock,
									};
									return (
										<StatusBadge type={config.type} icon={config.icon}>
											{config.label}
										</StatusBadge>
									);
								})()}
							</div>

							{fundReturn.status === "pending" && (
								<Button
									onClick={handleOpenBroadcastDialog}
									className="w-full"
									disabled={broadcastMutation.isPending}
								>
									<Radio className="mr-2 h-4 w-4" />
									Broadcast Transaction
								</Button>
							)}

							{fundReturn.transactionHash && (
								<Button variant="outline" className="w-full" asChild>
									<a
										href={`https://blockscan.com/tx/${fundReturn.transactionHash}`}
										target="_blank"
										rel="noopener noreferrer"
									>
										<ExternalLink className="mr-2 h-4 w-4" />
										View on Blockchain
									</a>
								</Button>
							)}
						</div>

						<Separator />

						<div className="space-y-4">
							<h3 className="text-lg font-semibold">Transaction Details</h3>
							<div className="space-y-3">
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">Network</span>
									<div className="flex items-center gap-2">
										<CryptoIcon
											ticker={fundReturn.blockchain}
											className="h-4 w-4"
										/>
										<span className="text-sm font-medium">
											{fundReturn.blockchain}
										</span>
									</div>
								</div>
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">Amount</span>
									<div className="flex items-center gap-2">
										<CryptoIcon
											ticker={fundReturn.currency}
											className="h-4 w-4"
										/>
										<span className="font-mono text-sm font-medium">
											{fundReturn.amount} {fundReturn.currency}
										</span>
									</div>
								</div>
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">
										USD Value
									</span>
									<span className="font-mono text-sm">
										${fundReturn.usdAmount}
									</span>
								</div>
							</div>
						</div>

						<Separator />

						<div className="space-y-4">
							<h3 className="text-lg font-semibold">Addresses</h3>
							<div className="space-y-3">
								<div className="flex flex-col gap-1">
									<span className="text-sm text-muted-foreground">
										From (Wallet)
									</span>
									<div className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded">
										<span className="font-mono text-xs break-all">
											{fundReturn.walletAddress}
										</span>
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6 flex-shrink-0"
											onClick={() =>
												handleCopyAddress(fundReturn.walletAddress)
											}
										>
											{copiedAddress === fundReturn.walletAddress ? (
												<Check className="h-3 w-3 text-green-600" />
											) : (
												<Copy className="h-3 w-3" />
											)}
										</Button>
									</div>
								</div>
								<div className="flex flex-col gap-1">
									<span className="text-sm text-muted-foreground">
										To (Destination)
									</span>
									<div className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded">
										<span className="font-mono text-xs break-all">
											{fundReturn.destinationAddress}
										</span>
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6 flex-shrink-0"
											onClick={() =>
												handleCopyAddress(fundReturn.destinationAddress)
											}
										>
											{copiedAddress === fundReturn.destinationAddress ? (
												<Check className="h-3 w-3 text-green-600" />
											) : (
												<Copy className="h-3 w-3" />
											)}
										</Button>
									</div>
								</div>
								{fundReturn.transactionHash && (
									<div className="flex flex-col gap-1">
										<span className="text-sm text-muted-foreground">
											Transaction Hash
										</span>
										<div className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded">
											<span className="font-mono text-xs break-all">
												{fundReturn.transactionHash}
											</span>
											<Button
												variant="ghost"
												size="icon"
												className="h-6 w-6 flex-shrink-0"
												onClick={() => {
													if (fundReturn.transactionHash) {
														handleCopyAddress(fundReturn.transactionHash);
													}
												}}
											>
												{copiedAddress === fundReturn.transactionHash ? (
													<Check className="h-3 w-3 text-green-600" />
												) : (
													<Copy className="h-3 w-3" />
												)}
											</Button>
										</div>
									</div>
								)}
							</div>
						</div>

						<Separator />

						<div className="space-y-4">
							<h3 className="text-lg font-semibold">Reason</h3>
							<div className="p-3 bg-muted/50 rounded-lg">
								<p className="text-sm whitespace-pre-wrap">
									{fundReturn.reason}
								</p>
							</div>
						</div>

						<Separator />

						<div className="space-y-4">
							<h3 className="text-lg font-semibold">Metadata</h3>
							<div className="space-y-3">
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">
										Created By
									</span>
									<span className="text-sm font-medium">
										{fundReturn.createdBy}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">Created</span>
									<TimeLabel time={fundReturn.createdAt} />
								</div>
								<div className="flex justify-between">
									<span className="text-sm text-muted-foreground">Updated</span>
									<TimeLabel time={fundReturn.updatedAt} />
								</div>
							</div>
						</div>

						{fundReturn.status === "failed" && (
							<>
								<Separator />
								<div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
									<div className="flex items-start gap-3">
										<AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
										<div>
											<h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
												Transaction Failed
											</h4>
											<p className="text-sm text-red-700 dark:text-red-300">
												This fund return failed to complete. Check the
												transaction details or contact support.
											</p>
										</div>
									</div>
								</div>
							</>
						)}
					</div>
				)}
			</EntityDrawer>

			<Dialog
				open={showBroadcastDialog}
				onOpenChange={(open) => !open && handleCloseBroadcastDialog()}
			>
				<DialogContent className="sm:max-w-[400px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Radio className="h-5 w-5" />
							Broadcast Transaction
						</DialogTitle>
						<DialogDescription>
							This will broadcast the transaction to the blockchain. This action
							is irreversible.
						</DialogDescription>
					</DialogHeader>

					{fundReturn && (
						<div className="space-y-4 py-4">
							<div className="rounded-lg border p-3 bg-muted/50 space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Amount</span>
									<span className="font-medium">
										{fundReturn.amount} {fundReturn.currency}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">To</span>
									<span className="font-mono text-xs">
										{fundReturn.destinationAddress.slice(0, 12)}...
									</span>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="broadcast-totp">2FA Code</Label>
								<Input
									id="broadcast-totp"
									placeholder="6-digit code"
									value={totpCode}
									onChange={(e) =>
										setTotpCode(e.target.value.replace(/\D/g, ""))
									}
									disabled={broadcastMutation.isPending}
									maxLength={6}
									inputMode="numeric"
								/>
							</div>

							<div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
								<p className="text-sm text-red-800 dark:text-red-200">
									This action cannot be undone.
								</p>
							</div>
						</div>
					)}

					<DialogFooter>
						<Button
							variant="outline"
							onClick={handleCloseBroadcastDialog}
							disabled={broadcastMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={handleBroadcast}
							disabled={broadcastMutation.isPending || totpCode.length !== 6}
						>
							{broadcastMutation.isPending ? "Broadcasting..." : "Broadcast"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};
